import type { CSSProperties, ReactNode } from 'react';
import { S, t, type Leaf } from '../i18n/strings';
import { useActions, useSteve } from '../state/context';
import type { Skin } from '../state/types';
import type { Fish } from './april';
import styles from './PaperFish.module.css';
import { Steve } from './Steve';

/**
 * Les poissons en papier du mot secret `poisson`.
 *
 * Un calque en `position: fixed`, transparent aux clics sauf sur les découpes
 * elles-mêmes : le site continue de marcher normalement pendant qu'on lui colle
 * des poissons dans le dos. C'est toute la différence avec l'overlay du mot
 * `orange`, qui prend le focus et bloque la page.
 *
 * Chaque découpe est un bouton — on la décolle en cliquant dessus, au doigt
 * comme au clavier. Il n'y a pas de commande pour en coller une : ça, c'est le
 * secret.
 *
 * **La onzième, c'est Steve**, et c'est le seul endroit où la découpe ne prend
 * pas le vocabulaire du skin : il est dessiné par son propre composant, donc
 * orange, comme partout ailleurs.
 */

/** L'étiquette écrite sur la découpe, dans le vocabulaire du skin. */
const TAGS: Record<Skin, Leaf> = S.april.tags;

export function PaperFish(): ReactNode {
  const { fish, lang, skin } = useSteve();
  const { peelFish } = useActions();

  if (fish.length === 0) return null;

  return (
    <div className={styles.layer} data-april-skin={skin}>
      {fish.map((one) => (
        <PaperCut
          key={one.id}
          fish={one}
          label={t(one.steve ? S.april.peel_steve : S.april.peel, lang)}
          tag={t(one.steve ? S.april.steve_tag : TAGS[skin], lang)}
          onPeel={() => {
            peelFish(one.id);
          }}
        />
      ))}
    </div>
  );
}

interface CutProps {
  readonly fish: Fish;
  readonly label: string;
  readonly tag: string;
  readonly onPeel: () => void;
}

function PaperCut({ fish, label, tag, onPeel }: CutProps): ReactNode {
  /*
   * Position et inclinaison sont des données, pas du style : elles sortent d'un
   * tirage. Elles passent donc par des variables CSS plutôt que par des
   * propriétés en dur — la feuille garde la main sur ce qu'elle en fait, et la
   * CSP stricte n'a rien à redire à un attribut `style` sans code.
   */
  const style = {
    '--fish-x': `${String(fish.x)}%`,
    '--fish-y': `${String(fish.y)}%`,
    '--fish-tilt': `${String(fish.tilt)}deg`,
  } as CSSProperties;

  return (
    <button
      type="button"
      className={styles.cut}
      style={style}
      data-cut={fish.cut}
      data-steve={fish.steve ? '' : undefined}
      aria-label={label}
      onClick={onPeel}
    >
      <span className={styles.tape} aria-hidden="true" />
      {fish.steve ? (
        <span className={styles.steveHolder} aria-hidden="true">
          <Steve bobDuration="0s" />
        </span>
      ) : (
        <span className={styles.paperFish} aria-hidden="true">
          <span className={styles.tail} />
          <span className={styles.body} />
          <span className={styles.eye} />
        </span>
      )}
      <span className={styles.tag}>{tag}</span>
    </button>
  );
}
